import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "../ui/Button";

interface ErrorBoundaryProps { children: ReactNode }
interface ErrorBoundaryState { error: Error | null }

export function ErrorScreen({ error }: { error: Error }) {
  return (
    <main className="fatal-error">
      <div className="fatal-error__mark"><AlertTriangle size={28} /></div>
      <p className="eyebrow">Something went wrong</p>
      <h1>The workspace could not be displayed.</h1>
      <p>A display error does not normally clear local data. Reload to try again; once the workspace opens, export a backup if you do not already have one.</p>
      <Button onClick={() => window.location.reload()}>Reload workspace</Button>
      {import.meta.env.DEV ? <details>
        <summary>Technical details</summary>
        <code>{error.name}</code>
      </details> : null}
    </main>
  );
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (import.meta.env.DEV) {
      console.error("Du'a Workspace encountered a display error", error.name, info.componentStack);
    }
  }

  render() {
    if (!this.state.error) return this.props.children;
    return <ErrorScreen error={this.state.error} />;
  }
}
